<template>
  <div class="bills-delivery">
    <div class="bills-delivery-top">
      <h2 class="bills-delivery-title ">{{ $t('billsDelivery.billsDeliveryTitle') }}</h2>
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="!listLoading && contractsList.length > 0" class="e-bill-wrapper" :key="'contractList'">
        <p class="bills-delivery-description" v-html="$t('billsDelivery.billsDeliveryDescription')"></p>
        <bill-card v-for="(contract, index) in contractsList"
                   :key="'contract' + index"
                   :customer-name="contract.firstName"
                   :customer-last-name="contract.lastName"
                   :customer-description="contract.description"
                   :service-name="contract.title"
                   :delivery-address="$t('billsDelivery.addressLabel')"
                   :address-name="contract.ebillEmail || contract.formattedAddress"
                   :e-bill-active="!!contract.ebillEmail"
                   :type="contract.type"
                   @activate-address="activateBillAddress(contract.customerId)"
                   @deactivate-address="deactivateBillAddress(contract.customerId)"
                   @change-address="changeEmailAddress(contract.customerId, contract.ebillEmail)"/>
      </div>
      <div v-else-if="!contractsList.length && !listLoading" class="prepaid-users-message">
        <img src="/assets/images/no-results.svg" width="112" height="112" alt=""/>
        <h3 class="title">{{ $t('billsOverview.noResultsTitle') }}</h3>
        <p class="description">{{ $t('billsDelivery.noResultsSubtitle') }}</p>
      </div>
      <loader-with-message v-else
                           :key="'loader2'"
                           :has-error="dataFetchError"
                           @retry="getContracts"/>
    </transition>
    <modal :visible="changeEmailAddressModal"
           @modal-close="resetData">
      <div class="change-email-header">
        <h2 v-if="activateAddress" class="change-email-title">{{ $t('billsDelivery.activateBillTitle') }}</h2>
        <h2 v-else class="change-email-title">{{ $t('billsDelivery.changeEmailAddress') }}</h2>
        <p v-if="activateAddress" class="change-email-subtitle">{{ $t('billsDelivery.activateBillDescription') }}</p>
        <p v-else class="change-email-subtitle">{{ $t('billsDelivery.newEmailAddress') }}</p>
      </div>
      <validation-observer ref="ebillEmailObs"
                           tag="form"
                           @submit.prevent="submitEmailInput">
        <div class="email-form">
          <div class="input-wrapper">
            <base-v-input v-model="emailInput"
                          id="email"
                          ref="email"
                          container-class="emailAddress"
                          name="email"
                          :submitted="emailAddressSubmitted"
                          :label="$t('registration.emailLabel')"
                          rules="required|noSpaces|email"
                          :placeholder="$t('billsDelivery.emailAddressPlaceholder')"
                          vid="email">
            </base-v-input>
          </div>
          <base-button :disabled="!emailInputFilled || buttonDisabled"
                       :loading="dataLoading" class="save-btn" type="submit">
            {{ $t('billsDelivery.saveBtn') }}
          </base-button>
        </div>
      </validation-observer>
    </modal>
    <bills-change-address-modal :visible="changeHomeAddressModalVisible"
                                @close="changeHomeAddressModalVisible = false"
                                :loading="citiesLoading"
                                :city-list="citiesList"
                                @submitted="handleAddressChange"/>
  </div>
</template>

<script>
import Modal from '@/components/common/modal/modal'
import BillCard from './bill-card'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import BillsChangeAddressModal from '@/components/bills/bills-change-address-modal'
import LoaderWithMessage from '@/components/common/loader-with-message'

export default {
  name: 'bills-delivery',
  components: { LoaderWithMessage, BillsChangeAddressModal, Modal, BillCard },
  data () {
    return {
      changeEmailAddressModal: false,
      emailInput: '',
      buttonDisabled: false,
      emailAddressSubmitted: false,
      homeAddressSubmitted: false,
      changeHomeAddressModalVisible: false,
      contractsList: [],
      activateAddress: false,
      ebillEmail: '',
      customerId: '',
      dataLoading: false,
      citiesLoading: false,
      citiesList: [],
      listLoading: true,
      oldEmail: '',
      dataFetchError: false
    }
  },
  mixins: [errorMessageHandler],
  methods: {
    fetchUserInfo () {
      this.$apiRequest.get('user/info')
        .then(response => {
          this.oldEmail = response.data.displayUid
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    getContracts () {
      this.listLoading = true
      this.dataFetchError = false
      this.$apiRequest.get('user/bills/contracts')
        .then(response => {
          this.contractsList = response.data.contracts
          this.listLoading = false
        })
        .catch(() => {
          this.dataFetchError = true
          // this.handleErrorMessage(e.response.data)
        })
    },
    changeEmailAddress (customerId, email) {
      if (email) {
        this.changeEmailAddressModal = true
        this.activateAddress = false
        this.emailInput = ''
      } else {
        this.changeHomeAddressModalVisible = true
        if (!this.citiesList.length) {
          this.fetchLocations()
        }
      }
      this.customerId = customerId
    },
    deactivateBillAddress (customerId) {
      this.$apiRequest.post(`user/bills/ebill?customerId=${customerId}`)
        .then(response => {
          if (response.status === 200) {
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successBillDeactivationMessage')
            })
            this.getContracts()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    activateBillAddress (customerId) {
      this.customerId = customerId
      this.activateAddress = true
      this.changeEmailAddressModal = true
      this.emailInput = this.oldEmail
    },
    activateChangeBillApi () {
      this.dataLoading = true
      this.$apiRequest.post(`user/bills/ebill?customerId=${this.customerId}&ebillEmail=${this.emailInput}`)
        .then(response => {
          if (response.status === 200) {
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successBillActivationMessage')
            })
            this.changeEmailAddressModal = false
            this.getContracts()
            this.resetData()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.dataLoading = false
        })
    },
    async submitEmailInput () {
      const valid = await this.$refs.ebillEmailObs.validate()
      this.emailAddressSubmitted = true
      if (valid) {
        this.activateChangeBillApi()
      }
    },
    resetData () {
      this.customerId = ''
      this.changeEmailAddressModal = false
    },
    fetchLocations () {
      this.citiesLoading = true
      this.$apiRequest.get('countries/cities')
        .then(response => {
          this.citiesList = response.data.cities
          this.citiesLoading = false
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.citiesLoading = false
        })
    },
    handleAddressChange (data) {
      this.citiesLoading = true

      const dataToSend = {
        paymentAddress: data
      }
      dataToSend.customerId = this.customerId
      this.$apiRequest.put('user/addresses/edit', dataToSend)
        .then(response => {
          // handle success
          if (response.status >= 200 && response.status < 400) {
            this.toast('success', {
              message: this.$t('billsDelivery.addressChangeSuccess')
            })
            this.getContracts()
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.changeHomeAddressModalVisible = false

          this.$nextTick(() => {
            this.citiesLoading = false
          })
        })
    }
  },
  computed: {
    emailInputFilled () {
      return !!this.emailInput
    }
  },
  mounted () {
    this.getContracts()
    this.fetchUserInfo()
  }
}
</script>

<style scoped lang="scss" src="./bills-comp-styles/bills-delivery.scss"></style>
